import { useEffect, useRef, useState } from 'react';
import { Button, Coin } from '@/components/ui';
import {
  confirmMockOrder,
  verifyOrder,
  waitForOrderPaid,
  coinsFromOrder,
  rupees,
  type CreatedOrder,
} from '@/lib/data/wallet';
import { openCheckout, CheckoutDismissed } from '@/lib/razorpay';
import {
  openCheckout as openCashfreeCheckout,
  CheckoutDismissed as CashfreeDismissed,
  CheckoutFailed,
} from '@/lib/cashfree';
import { useAuth } from '@/lib/auth';

/**
 * The confirm step.
 *
 * Two gateways behind one button. On the dev mock it settles the order
 * directly; on Razorpay it opens their sheet and hands the result back to be
 * verified. The surrounding flow — pick a pack, confirm, see coins — is the
 * same either way, which is the point of the seam in services/payments.ts.
 *
 * On the mock it says plainly that no money is being taken. A fake payment
 * that looks real is how someone demos it to an investor and only later finds
 * out.
 */
export default function PayDialog({
  order,
  onDone,
  onCancel,
}: {
  order: CreatedOrder;
  onDone: (coinsCredited: number) => void;
  onCancel: () => void;
}) {
  const { profile, session } = useAuth();
  const [busy, setBusy] = useState(false);
  /*
   * Set while the Cashfree webhook is being waited on. The sheet has closed
   * and the money has left by this point, so "Processing…" reads as if nothing
   * has happened yet — which is the moment someone decides to pay twice.
   */
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<HTMLDivElement>(null);

  const isFee = order.purpose !== 'coin_purchase';
  const totalCoins = (order.coins ?? 0) + order.bonusCoins;

  // Escape closes, and focus moves into the dialog so a keyboard user is not
  // stranded behind it.
  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy && !confirming) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, confirming, onCancel]);

  async function pay() {
    setBusy(true);
    setError(null);

    try {
      if (order.provider === 'mock') {
        const result = await confirmMockOrder(order.orderId);
        onDone(result.coinsCredited);
        return;
      }

      if (order.provider === 'cashfree') {
        if (!order.paymentSessionId) throw new Error('That payment could not be started.');

        await openCashfreeCheckout(order.paymentSessionId);
        setConfirming(true);

        /*
         * Cashfree brings back no signed payload, so the browser cannot prove
         * the payment — only the webhook can, and it lands a moment later.
         * Wait for THIS ORDER to read as paid rather than claiming success.
         */
        const wallet = await waitForOrderPaid(order.orderId);

        setConfirming(false);

        if (!wallet) {
          setError(
            'Payment received. It is taking a moment to confirm — this page will catch up ' +
              'shortly. Do not pay again.',
          );
          setBusy(false);
          return;
        }

        onDone(coinsFromOrder(wallet, order.orderId));
        return;
      }

      const checkout = await openCheckout({
        gatewayOrderId: order.gatewayOrderId,
        amountPaise: order.amountPaise,
        description: isFee ? 'Platform fee' : `${totalCoins.toLocaleString('en-IN')} coins`,
        name: profile?.display_name ?? null,
        email: session?.user.email ?? null,
      });

      const result = await verifyOrder(order.orderId, checkout);
      onDone(result.coinsCredited);
    } catch (err) {
      // Closing the sheet is not a failure — the user changed their mind, and
      // a red error for that is both wrong and alarming when money is involved.
      if (err instanceof CheckoutDismissed || err instanceof CashfreeDismissed) {
        setBusy(false);
        return;
      }

      /*
       * Anything else here means the payment MAY have gone through — a network
       * drop between Razorpay taking the money and our verify call landing
       * looks exactly like a failure from in here. So the wording never says
       * the payment failed, because the webhook may still credit it moments
       * from now.
       */
      setConfirming(false);

      /*
       * The gateway refused the payment. The outcome is known, so say so —
       * the hedged wording below is for the genuinely uncertain case, and
       * using it here would have someone checking their bank statement over a
       * card that simply declined.
       */
      if (err instanceof CheckoutFailed) {
        setError(`${err.message} Nothing was charged — you can try again or use another method.`);
        setBusy(false);
        return;
      }

      setError(
        err instanceof Error
          ? `${err.message} If money left your account, your coins will appear shortly — do not pay again.`
          : 'Something went wrong. If money left your account, your coins will appear shortly — do not pay again.',
      );
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgb(0 0 0 / 0.5)' }}
      onClick={() => !busy && !confirming && onCancel()}
      role="presentation"
    >
      <div
        ref={confirmRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pay-title"
        className="animate-rise w-full max-w-sm rounded-2xl p-6 outline-none"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          boxShadow: '0 24px 48px rgb(0 0 0 / 0.24)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="pay-title" className="text-xl font-semibold">
          {isFee ? 'Confirm payment' : 'Confirm purchase'}
        </h2>

        <div
          className="mt-5 flex flex-col gap-3 rounded-xl px-4 py-4"
          style={{ background: 'var(--surface-2)' }}
        >
          <Row label={isFee ? 'Platform fee' : 'You get'}>
            {isFee ? (
              <span className="font-semibold">one-time</span>
            ) : (
              <span className="flex items-center gap-1.5 font-semibold">
                <Coin />
                {totalCoins.toLocaleString('en-IN')} coins
              </span>
            )}
          </Row>

          {!isFee && order.bonusCoins > 0 && (
            <Row label="Includes bonus">
              <span className="text-[var(--teal)]">
                +{order.bonusCoins.toLocaleString('en-IN')}
              </span>
            </Row>
          )}

          <div className="h-px" style={{ background: 'var(--line)' }} />

          <Row label="To pay">
            <span className="text-lg font-semibold">{rupees(order.amountPaise)}</span>
          </Row>
        </div>

        {order.provider === 'mock' && (
          <p
            className="mt-4 rounded-lg px-3 py-2.5 text-xs leading-relaxed"
            style={{ background: 'var(--marigold-soft)', color: 'var(--marigold)' }}
          >
            <strong>Test mode.</strong> No money will be taken and no card is needed. Coins are
            credited straight away so the rest of the app can be used.
          </p>
        )}

        {error && (
          <p className="mt-4 text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          <Button onClick={() => void pay()} disabled={busy} full>
            {confirming
              ? 'Confirming your payment…'
              : busy
                ? 'Processing…'
                : `Pay ${rupees(order.amountPaise)}`}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-[var(--ink-2)]">{label}</span>
      {children}
    </div>
  );
}
